import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, Account } from '../services/api'
import { X, Edit2, Save } from 'lucide-react'

export default function Accounts() {
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.getAccounts(),
  })

  const toggleMutation = useMutation({
    mutationFn: (accountId: number) => api.toggleAccountExclusion(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      queryClient.invalidateQueries({ queryKey: ['aggregate'] })
      queryClient.invalidateQueries({ queryKey: ['metrics'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Account> }) =>
      api.updateAccount(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      setEditingId(null)
    },
  })

  const toggleCanGoShort = useMutation({
    mutationFn: (account: Account) =>
      api.updateAccount(account.id, { can_go_short: !account.can_go_short }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
    },
  })

  const startEditing = (account: Account) => {
    setEditingId(account.id)
    setEditName(account.account_name || '')
  }

  const saveEdit = (accountId: number) => {
    updateMutation.mutate({ id: accountId, data: { account_name: editName } })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditName('')
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Accounts</h1>
      </div>

      <div className="card">
        <p className="text-sm text-gray-600 mb-6">
          Manage your accounts here. Toggle the exclusion switch to include or exclude accounts
          from the aggregate portfolio calculations.
        </p>

        {data?.accounts.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">
              No accounts yet. Upload a portfolio statement to see your accounts.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Display Name
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Include in Aggregate
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Can Go Short
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Min Cash %
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data?.accounts.map((account) => (
                  <tr key={account.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">
                        {account.account_number}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingId === account.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="input max-w-xs"
                            placeholder="Display name"
                            autoFocus
                          />
                          <button
                            onClick={() => saveEdit(account.id)}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                            disabled={updateMutation.isPending}
                          >
                            <Save className="h-4 w-4" />
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-600">
                          {account.account_name || '-'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => toggleMutation.mutate(account.id)}
                        disabled={toggleMutation.isPending}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          !account.exclude_from_aggregate
                            ? 'bg-primary-600'
                            : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            !account.exclude_from_aggregate
                              ? 'translate-x-6'
                              : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => toggleCanGoShort.mutate(account)}
                        disabled={toggleCanGoShort.isPending}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          account.can_go_short
                            ? 'bg-primary-600'
                            : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            account.can_go_short
                              ? 'translate-x-6'
                              : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="inline-flex items-center">
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          max="100"
                          value={account.min_cash_pct ?? ''}
                          onChange={(e) => {
                            const value = e.target.value === '' ? null : parseFloat(e.target.value)
                            updateMutation.mutate({ id: account.id, data: { min_cash_pct: value } })
                          }}
                          placeholder="-"
                          className="w-14 px-2 py-1 text-right text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <span className="ml-1 text-sm text-gray-500">%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {editingId !== account.id && (
                        <button
                          onClick={() => startEditing(account)}
                          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg"
                          title="Edit name"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="card bg-gray-50">
        <h3 className="font-medium text-gray-900 mb-2">About Account Aggregation</h3>
        <p className="text-sm text-gray-600">
          When an account is included in the aggregate, its positions and values will be combined
          with other included accounts to show your total portfolio. Excluded accounts will still
          be available for individual viewing but won't affect the aggregate totals shown on the
          dashboard.
        </p>
      </div>
    </div>
  )
}
